import React from "react";
import { ArrowRight } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const STEPS = [
  {
    n: "01",
    title: "Paste your URL",
    body: "Give us your homepage. In 30 seconds we generate your brand profile, ICPs and buyer keywords.",
  },
  {
    n: "02",
    title: "Run a scan",
    body: "Hit \"Find customers\". We sweep Reddit, forums, LinkedIn and YouTube - analyze every post, rank the best.",
  },
  {
    n: "03",
    title: "Save & reply",
    body: "Save promising leads to a board. Draft a plain-English reply in one click. Go close deals - not build dashboards.",
  },
];

// Solid number-card colors per step: 1 green, 2 blue, 3 yellow.
const STEP_CARD = [
  "bg-lm-primary text-white",     // 01 — green
  "bg-blue-600 text-white",       // 02 — blue
  "bg-yellow-400 text-lm-text",   // 03 — yellow (dark text for contrast)
];

export default function HowItWorks() {
  const [headRef, headVisible] = useReveal();
  const [rowRef, rowVisible] = useReveal(0.15);

  return (
    <section id="how" className="relative overflow-hidden lm-cv">
      <div className="absolute inset-0 pointer-events-none">
        <div className="lm-parallax lm-parallax-slow absolute -top-24 right-20 w-80 h-80 rounded-full bg-lm-tint2/60 blur-3xl" />
      </div>
      <div className="relative max-w-6xl mx-auto px-5 md:px-10 py-20 md:py-24">
        <div
          ref={headRef}
          className={`max-w-3xl mx-auto text-center mb-14 md:mb-16 lm-reveal ${headVisible ? "is-visible" : ""}`}
        >
          <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-3">
            How it works
          </div>
          <h2 className="text-3xl md:text-5xl font-display font-semibold leading-tight tracking-tight">
            Three steps from signup to shortlist.
          </h2>
        </div>

        <div
          ref={rowRef}
          className="relative mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 lm-stagger max-w-5xl"
        >
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              style={{ "--i": i }}
              className={`relative flex flex-col items-center text-center lm-reveal ${rowVisible ? "is-visible" : ""}`}
            >
              {/* Right arrow to the next step (desktop only) */}
              {i < STEPS.length - 1 && (
                <ArrowRight
                  aria-hidden="true"
                  className="hidden md:block absolute top-8 -right-5 -translate-y-1/2 w-7 h-7 text-lm-text3 z-20"
                  strokeWidth={2.2}
                />
              )}
              <div className="mb-5 relative">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center relative z-10 shadow-sm ${STEP_CARD[i] || "bg-lm-surface text-lm-secondary"}`}>
                  <span className="font-mono font-bold text-lg">{s.n}</span>
                </div>
              </div>
              <h3 className="text-xl font-display font-semibold mb-2 tracking-tight">{s.title}</h3>
              <p className="text-lm-text2 leading-relaxed max-w-[280px]">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
